import { getApplicationInsights } from '@/lib/application-insights';
import { isLocalDevelopment } from '@/utils/is-local-host';

export type AuthenticationFailureStage =
  'redirect_initiation' | 'callback' | 'backend_authorization' | 'session_restore' | 'bootstrap';

export type AuthenticationFailureReasonCode =
  | 'redirect_failed'
  | 'provider_error'
  | 'account_missing'
  | 'processing_failed'
  | 'backend_unauthorized'
  | 'cached_session_invalid'
  | 'bootstrap_failed';

export interface AuthenticationFailureTelemetry {
  stage: AuthenticationFailureStage;
  reasonCode: AuthenticationFailureReasonCode;
  providerErrorCode?: string;
  entraCorrelationId?: unknown;
  unexpectedError?: unknown;
  flushBeforeNavigation?: boolean;
}

const SAFE_TOKEN_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TELEMETRY_SCHEMA_VERSION = '2';
const UNKNOWN_VALUE = 'unknown';

const normalizeSafeToken = (value: unknown): string => {
  return typeof value === 'string' && SAFE_TOKEN_PATTERN.test(value) ? value : UNKNOWN_VALUE;
};

const normalizeCorrelationId = (value: unknown): string | undefined => {
  return typeof value === 'string' && GUID_PATTERN.test(value) ? value : undefined;
};

const readMsalErrorMetadata = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return {};
  }

  const candidate = error as Record<string, unknown>;

  return {
    msalErrorCode:
      candidate.errorCode === undefined ? undefined : normalizeSafeToken(candidate.errorCode),
    msalSubError:
      candidate.subError === undefined ? undefined : normalizeSafeToken(candidate.subError),
    entraCorrelationId: normalizeCorrelationId(candidate.correlationId),
  };
};

/** Initializes browser telemetry before MSAL starts callback token redemption. */
export const initializeAuthenticationTelemetry = (): void => {
  try {
    if (isLocalDevelopment()) return;
    getApplicationInsights();
  } catch {
    // Telemetry initialization must never interrupt authentication.
  }
};

/**
 * Emits one privacy-safe authentication failure event. Unexpected failures also
 * create a sanitized exception, without forwarding the original message or stack.
 */
export const trackAuthenticationFailure = (telemetry: AuthenticationFailureTelemetry): void => {
  try {
    if (isLocalDevelopment()) return;

    const appInsights = getApplicationInsights();
    if (!appInsights) return;

    const hasUnexpectedError = Object.prototype.hasOwnProperty.call(telemetry, 'unexpectedError');
    const msalMetadata = readMsalErrorMetadata(telemetry.unexpectedError);
    const entraCorrelationId =
      normalizeCorrelationId(telemetry.entraCorrelationId) ?? msalMetadata.entraCorrelationId;
    const properties: Record<string, string> = {
      stage: telemetry.stage,
      reasonCode: telemetry.reasonCode,
      provider: 'microsoft-entra-id',
      environment: process.env.NEXT_PUBLIC_APP_ENV ?? UNKNOWN_VALUE,
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? UNKNOWN_VALUE,
      schemaVersion: TELEMETRY_SCHEMA_VERSION,
    };

    if (telemetry.providerErrorCode !== undefined) {
      properties.providerErrorCode = normalizeSafeToken(telemetry.providerErrorCode);
    }

    if (hasUnexpectedError) {
      properties.errorType =
        telemetry.unexpectedError instanceof Error
          ? normalizeSafeToken(telemetry.unexpectedError.name)
          : UNKNOWN_VALUE;
    }

    if (msalMetadata.msalErrorCode !== undefined) {
      properties.msalErrorCode = msalMetadata.msalErrorCode;
    }

    if (msalMetadata.msalSubError !== undefined) {
      properties.msalSubError = msalMetadata.msalSubError;
    }

    if (entraCorrelationId !== undefined) {
      properties.entraCorrelationId = entraCorrelationId;
    }

    if (typeof navigator !== 'undefined') {
      properties.browserReportedOnline = String(navigator.onLine);
    }

    appInsights.trackEvent({
      name: 'AuthenticationFailed',
      properties,
    });

    if (hasUnexpectedError) {
      appInsights.trackException({
        exception: new Error('Unexpected authentication failure'),
        properties,
      });
    }

    if (telemetry.flushBeforeNavigation) {
      appInsights.onunloadFlush();
    }
  } catch {
    // Telemetry must never interrupt authentication or redirect behavior.
  }
};
